import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { api } from '@/api/client';
import { WhatsappHistory } from '@/features/whatsapp/WhatsappHistory';

type Ref = { template?: string; language?: string };

interface MerchantWa {
  merchantId: number; merchant: string; billingMode: string; walletBalance: number;
  globallyEnabled: boolean; enabled: boolean; pricePerMessage: number; gstPct: number;
  events: Array<{ event: string; label: string; enabled: boolean; B2C: Ref; B2B: Ref; defaultB2C: Ref; defaultB2B: Ref }>;
}

/**
 * Merchant → WhatsApp (internal staff only): switch WhatsApp on for this merchant, set its price per
 * message, pick the events, and optionally give an event a template of its own. The merchant sees
 * the result read-only in its portal (Settings → SMS & WhatsApp).
 */
export function MerchantWhatsappTab({ merchantId }: { merchantId: number }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['whatsapp', 'merchant', merchantId], queryFn: async () => (await api.get<MerchantWa>(`/whatsapp/merchants/${merchantId}`)).data });
  const { data: tpl } = useQuery({ queryKey: ['whatsapp', 'templates'], queryFn: async () => (await api.get<{ items: any[] }>('/whatsapp/templates')).data });
  const approved = (tpl?.items ?? []).filter((t: any) => t.status === 'APPROVED');
  const [f, setF] = useState<MerchantWa | null>(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  const save = useMutation({
    mutationFn: async () => (await api.put(`/whatsapp/merchants/${merchantId}`, {
      enabled: f!.enabled, pricePerMessage: f!.pricePerMessage,
      events: f!.events.map((e) => ({ event: e.event, enabled: e.enabled, B2C: e.B2C, B2B: e.B2B })),
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['whatsapp', 'merchant', merchantId] }); toast.success('WhatsApp settings saved for this merchant'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });
  if (!f) return <div className="py-8 text-center"><Loader2 size={16} className="animate-spin inline text-slate-400" /></div>;
  const incl = Math.round(Number(f.pricePerMessage || 0) * (1 + f.gstPct / 100) * 100) / 100;
  const setEv = (i: number, patch: any) => setF({ ...f, events: f.events.map((e, j) => (j === i ? { ...e, ...patch } : e)) });

  return (
    <div className="space-y-5" data-testid="merchant-wa-tab">
      {!f.globallyEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          WhatsApp sending is switched OFF platform-wide (Settings → WhatsApp). This merchant's settings are kept and take effect when it is switched on.
        </div>
      )}
      <div className="flex flex-wrap items-end gap-6">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="mwa-enabled" />
          WhatsApp messages for {f.merchant}
        </label>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Price per message (₹, before GST)</label>
          <input type="number" step="0.01" min="0" value={f.pricePerMessage} onChange={(e) => setF({ ...f, pricePerMessage: e.target.value as any })} className="input w-36" data-testid="mwa-price" />
        </div>
        <div className="text-xs text-slate-500 pb-2">
          = <b>₹{incl.toFixed(2)}</b> incl. {f.gstPct}% GST · {f.billingMode === 'POSTPAID'
            ? 'POSTPAID — billed on a monthly WhatsApp invoice'
            : <>PREPAID — debited from the wallet (available ₹{f.walletBalance.toFixed(2)}, wallet + COD held — the figure the merchant sees); no balance, no message{f.walletBalance < incl && <b className="text-rose-600"> · balance too low: nothing will send</b>}</>}
        </div>
      </div>
      <table className="w-full text-sm border border-slate-200 rounded-lg">
        <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
          <tr><th className="text-left px-3 py-2">Event</th><th className="text-center px-3 py-2 w-24">Send</th><th className="text-left px-3 py-2">B2C template</th><th className="text-left px-3 py-2">B2B template</th></tr>
        </thead>
        <tbody>
          {f.events.map((e, i) => (
            <tr key={e.event} className="border-t border-slate-100">
              <td className="px-3 py-2 font-medium">{e.label}</td>
              <td className="px-3 py-2 text-center"><input type="checkbox" checked={e.enabled} onChange={(x) => setEv(i, { enabled: x.target.checked })} data-testid={`mwa-ev-${e.event}`} /></td>
              {(['B2C', 'B2B'] as const).map((type) => {
                const own = e[type] ?? {};
                const def = type === 'B2C' ? e.defaultB2C : e.defaultB2B;
                return (
                  <td key={type} className="px-3 py-2">
                    <select value={own.template ? `${own.template}|${own.language ?? ''}` : ''} className="input py-1 text-xs w-64" data-testid={`mwa-tpl-${e.event}-${type}`}
                      onChange={(x) => { const [template, language] = x.target.value.split('|'); setEv(i, { [type]: template ? { template, language } : {} }); }}>
                      <option value="">Default{def?.template ? `: ${def.template} (${def.language})` : ' — none set in Settings → WhatsApp'}</option>
                      {approved.map((t: any) => <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</option>)}
                    </select>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-end">
        <button onClick={() => save.mutate()} disabled={save.isPending} data-testid="mwa-save" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-60">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">Messages sent for this merchant</h3>
        <WhatsappHistory merchantId={merchantId} />
      </div>
    </div>
  );
}
